/**
 * The composer slot — item AE fills it with the real chat, and M6 moves it into
 * the chat pane. It lives here now because the rule it carries is item K's, and
 * that rule needs somewhere to be true:
 *
 * The input is **disabled with an explanation** whenever the agent is anything
 * but ready, never enabled-and-failing. A live box that swallows a message
 * because the agent is still starting is worse than a disabled one that says so.
 *
 * Submitting pings the agent for real, through `agentClient`, so the port it
 * reaches is measured rather than assumed — and the result names the port, which
 * is how "the next request went to the *new* port" is observable at all.
 */

import { useState } from "react";
import { describeAgent } from "../lib/agentClient";
import { agentClient, AgentUnavailableError, type AgentTarget } from "../lib/agentClient";

export default function Composer({ agent }: { agent: AgentTarget }) {
  const [result, setResult] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function ping(): Promise<void> {
    setBusy(true);
    try {
      // No base URL is passed in. The client resolves it now, for this request.
      const response = await agentClient.fetch("/health");
      const body = (await response.json()) as { pid?: number };
      const url = new URL(response.url);
      setResult(`${response.status} from port ${url.port} (agent pid ${body.pid ?? "?"})`);
    } catch (error: unknown) {
      setResult(
        error instanceof AgentUnavailableError
          ? error.message
          : `request failed: ${String(error)}`,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-composer style={{ marginTop: "1.5rem" }}>
      <input
        type="text"
        disabled={!agent.ok}
        placeholder={agent.ok ? "Ask something (chat arrives in M6)" : describeAgent(agent)}
        style={{ width: "26rem", padding: "0.5rem", fontSize: "1rem" }}
      />{" "}
      <button type="button" onClick={() => void ping()} disabled={!agent.ok || busy}>
        {busy ? "…" : "Ping agent"}
      </button>
      {result ? <p style={{ color: "#666" }}>{result}</p> : null}
    </div>
  );
}
